import { AxiosResponse } from "axios";
import {
  axiosAuthenticated,
  httpClient,
  uploadImageAndGetCdnUrl,
} from "../../../service/http";
import { CreateProviderPaymentPoints } from "../models/CreateProviderPaymentPoints";
import { GetProviderPaymentPointsSuccessResponse } from "../models/GetProviderPaymentPointsSuccessResponse";
import {
  CreateSalesChannel,
  MutateSalesChannelEgressConnectionPayload,
  SalesChannelEgressConnection,
  SalesChannelEgressConnectionsSuccessResponse,
  SalesChannelItem,
  SalesChannelListItem,
  SalesChannelSuccessResponse,
  SalesChannelsSuccessResponse,
  UpdateSalesChannel,
} from "../models/sales-channel";
import { OrganizationKeyName } from "../../../model/Organization";
import { LinkProductToShopType } from "../../../service/api-response/LinkProductToShop";
import { UnlinkProductToShopType } from "../../../service/api-response/UnlinkProductFromShop";
import {
  CreateShop,
  Shop,
  ShopSuccessResponse,
  UpdateShop,
} from "../model/ShopSuccessResponse";
import { ShopListSuccessResponse } from "../model/ShopListSuccessResponse";
import {
  DeletePayload,
  PostPayload,
  PutPayload,
  UpdateData,
} from "../../../model/http-crud-models";
import {
  TimeslotTemaplatesSuccessResponse,
  TimeslotTemplateCreate,
  TimeslotTemplateUpdate,
} from "../model/TimeslotTemplatesSuccessResponse";
import { UploadImage } from "../../../service/api-response/UploadImage";
import { AdyenStore } from "../model/AdyenStore";
import {
  MutateShopPaymentProvider,
  ShopPaymentProvider,
} from "../models/ShopPaymentProvider";

export const shopHttp = {
  getProviderPaymentPoints: async (
    organizationKeyName: string,
    shopId?: string
  ) => {
    const url = `/organizations/${organizationKeyName}/shops/${shopId}/provider-payment-points`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<GetProviderPaymentPointsSuccessResponse>
    >(url);
    return response.data.data;
  },

  insertProviderPaymentPoints: async ({
    organizationKeyName,
    shopId,
    data,
  }: CreateProviderPaymentPoints) => {
    const url = `/organizations/${organizationKeyName}/shops/${shopId}/provider-payment-points`;
    const response = await axiosAuthenticated.post<any, AxiosResponse>(
      url,
      data
    );

    return response.data;
  },

  getSalesChannels: async (
    organizationKeyName: string,
    shopId?: string
  ): Promise<SalesChannelListItem[]> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items?shop=${shopId}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<SalesChannelsSuccessResponse>
    >(url);
    const salesChannels = response.data.data;
    return salesChannels;
  },

  getSalesChannel: async (
    organizationKeyName: string,
    id?: string
  ): Promise<SalesChannelItem> => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<SalesChannelSuccessResponse>
    >(url);
    const salesChannel = response.data.data;
    return salesChannel;
  },

  getSalesChannelEgressConnections: async (
    organizationKeyName: string,
    id?: string
  ): Promise<SalesChannelEgressConnection[]> => {
    const response = await httpClient<SalesChannelEgressConnectionsSuccessResponse>(
      {
        url: `/organizations/${organizationKeyName}/sales-channels/items/${id}/egress-connections`,
      }
    );
    return response.data;
  },

  createSalesChannelEgressConnection: async ({
    data,
    organizationKeyName,
  }: PostPayload<MutateSalesChannelEgressConnectionPayload>) => {
    const { salesChannelId, ...rest } = data;
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/sales-channels/items/${salesChannelId}/egress-connections`,
      data: rest,
    });
  },

  updateSalesChannelEgressConnection: async ({
    data,
    organizationKeyName,
  }: PutPayload<MutateSalesChannelEgressConnectionPayload>) => {
    const { salesChannelId, id, ...rest } = data;
    return httpClient<{}>({
      method: "PUT",
      url: `/organizations/${organizationKeyName}/sales-channels/items/${salesChannelId}/egress-connections/${id}`,
      data: rest,
    });
  },

  deleteSalesChannelEgressConnection: async ({
    organizationKeyName,
    data,
  }: DeletePayload<{ salesChannelId: string }>) => {
    const { id, salesChannelId } = data;
    return httpClient<{}>({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/sales-channels/items/${salesChannelId}/egress-connections/${id}`,
    });
  },

  createSalesChannel: async ({
    data,
    organizationKeyName,
  }: CreateSalesChannel) => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);

    return response.data.id;
  },

  updateSalesChannel: async ({
    data,
    id,
    organizationKeyName,
  }: UpdateSalesChannel) => {
    const url = `/organizations/${organizationKeyName}/sales-channels/items/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  linkProductToShop: async ({
    organizationKeyName,
    productId,
    shopId,
  }: LinkProductToShopType) => {
    const response = await axiosAuthenticated.put(
      `/organizations/${organizationKeyName}/shop-link-product`,
      { shopId, productId }
    );

    return response.data.data;
  },

  unlinkProductToShop: async ({
    organizationKeyName,
    productId,
    shopId,
  }: UnlinkProductToShopType) => {
    const response = await axiosAuthenticated.put(
      `/organizations/${organizationKeyName}/shop-unlink-product`,
      { shopId, productId }
    );

    return response.data.data;
  },

  getShops: async (organizationKeyName?: OrganizationKeyName) => {
    const url = `/organizations/${organizationKeyName}/shops/items`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopListSuccessResponse>
    >(url);
    return response.data.data;
  },

  getShop: async (organizationKeyName: string, id?: string): Promise<Shop> => {
    const url = `/organizations/${organizationKeyName}/shops/items/${id}`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<ShopSuccessResponse>
    >(url);
    const shop = response.data.data;
    return shop;
  },

  updateShop: async ({ organizationKeyName, id, data }: UpdateShop) => {
    const url = `/organizations/${organizationKeyName}/shops/items/${id}`;
    const response = await axiosAuthenticated.put<any, AxiosResponse>(
      url,
      data
    );

    return response;
  },

  createShop: async ({ organizationKeyName, data }: CreateShop) => {
    const url = `/organizations/${organizationKeyName}/shops/items`;
    const response = await axiosAuthenticated.post<
      any,
      AxiosResponse<{ id: string }>
    >(url, data);

    return response.data.id;
  },

  listTimeslots: async (organizationKeyName: string, shopId: string) => {
    const url = `/organizations/${organizationKeyName}/shops/${shopId}/timeslot-templates`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<TimeslotTemaplatesSuccessResponse>
    >(url);
    const timeslots = response.data.timeslots;
    return timeslots;
  },

  copyAndSetTimeslotsFromShop: async ({
    organizationKeyName,
    data,
  }: PostPayload<{ sourceShopId: string; targetShopId: string }>) => {
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/timeslot-templates/overwrite`,
      data,
    });
  },

  createTimeslotTemplate: async ({
    organizationKeyName,
    data,
  }: PostPayload<TimeslotTemplateCreate & { shopId: string }>) => {
    const { shopId, ...rest } = data;
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/${shopId}/timeslot-templates/create`,
      data: rest,
    });
  },

  updateTimeslot: async ({
    organizationKeyName,
    id,
    data,
  }: UpdateData<TimeslotTemplateUpdate>) => {
    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/${id}/timeslot-templates/update`,
      data: {
        templateRequests: [
          {
            templateId: data.id,
            ...data,
          },
        ],
      },
    });
  },

  uploadAllergensPdf: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "shop-allergens-pdf",
    });
  },

  getAdyenStoresForOrganization: async (
    organizationKeyName: string
  ): Promise<AdyenStore[]> => {
    const url = `/organizations/${organizationKeyName}/adyen/stores/items`;
    const response = await axiosAuthenticated.get<
      any,
      AxiosResponse<AdyenStore[]>
    >(url);
    return response.data;
  },

  getPaymentProviders: async ({
    organizationKeyName,
    shopId,
  }: {
    organizationKeyName: string;
    shopId: string;
  }): Promise<ShopPaymentProvider[]> => {
    const url = `/organizations/${organizationKeyName}/shops/items/${shopId}/payment-providers`;
    return httpClient<ShopPaymentProvider[]>({
      method: "GET",
      url,
    });
  },

  getPaymentProvider: async ({
    organizationKeyName,
    shopId,
    id,
  }: {
    organizationKeyName: string;
    id: string;
    shopId: string;
  }): Promise<ShopPaymentProvider> => {
    const url = `/organizations/${organizationKeyName}/shops/items/${shopId}/payment-providers/items/${id}`;
    return httpClient<ShopPaymentProvider>({
      method: "GET",
      url,
    });
  },

  addShopPaymentProvider: async ({
    organizationKeyName,
    data: { shopId, ...rest },
  }: PostPayload<MutateShopPaymentProvider & { shopId: string }>) => {
    const response = await httpClient<{ id: string }>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shops/items/${shopId}/payment-providers`,
      data: rest,
    });
    return response.id;
  },

  deleteShopPaymentProvider: async ({
    organizationKeyName,
    shopId,
    id,
  }: {
    shopId: string;
    organizationKeyName: string;
    id: string;
  }) => {
    return httpClient<object>({
      method: "DELETE",
      url: `/organizations/${organizationKeyName}/shops/items/${shopId}/payment-providers/items/${id}`,
    });
  },

  updateShopPaymentProvider: async ({
    organizationKeyName,
    data: { shopId, id, ...rest },
  }: PutPayload<
    MutateShopPaymentProvider & { shopId: string; id: string }
  >) => {
    const response = await httpClient<{ id: string }>({
      method: "PUT",
      url: `/organizations/${organizationKeyName}/shops/items/${shopId}/payment-providers/items/${id}`,
      data: rest,
    });
    return response.id;
  },
};
